"use client";

import { Download } from "lucide-react";
import type { CompanyReportGridComponent } from "../../types";
import { RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { isExternalHref } from "../../utils/links";

const CARD_BG = "#00B862";
const COLS: Record<number, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

function toPerRow(v: unknown): 2 | 3 | 4 {
  const n = Number(v);
  return n >= 2 && n <= 4 ? (n as 2 | 3 | 4) : 3;
}

/** Company report grid renderer with download buttons. */
export function CompanyReportGridRenderer({ component }: { component: CompanyReportGridComponent }) {
  const { items, cardsPerRowTablet, cardsPerRowDesktop, downloadLabel, centerLastRow: centerLastRowOpt } = component.content;
  const locale = useLocale();
  const label = downloadLabel ?? t(locale, "download");
  const centerLastRow = centerLastRowOpt === true || centerLastRowOpt === "true";
  const tablet = toPerRow(cardsPerRowTablet);
  const desktop = toPerRow(cardsPerRowDesktop);
  const gridCols = `sm:grid-cols-2 md:${COLS[tablet]} lg:${COLS[desktop]}`;

  const renderCard = (item: (typeof items)[0], idx: number) => {
    const openInNewTab = isExternalHref(item.href);
    return (
      <div
        key={`${item.year}-${idx}`}
        className="flex h-full flex-col rounded-lg p-6 md:p-7 text-white ring-1 ring-black/10"
        style={{ backgroundColor: CARD_BG }}
      >
        <div className="flex flex-1 flex-col items-center text-center">
          <h3 className="text-base xl:text-lg font-bold md:text-[17px]">{item.title}</h3>
          <p className="mt-1 text-lg xl:text-xl font-bold md:text-xl">{item.year}</p>
          <p className="mt-2 w-full text-left text-sm xl:text-base opacity-90">
            {t(locale, "published")} {item.publishedDate}
          </p>
        </div>
        <div className="mt-4 flex justify-center">
          <RuntimeLink
            href={item.href}
            className="inline-flex items-center gap-2 rounded-lg border-2 bg-white px-4 py-2 text-sm xl:text-base font-semibold transition hover:opacity-90"
            style={{ borderColor: CARD_BG, color: CARD_BG }}
            {...(openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            {label}
            <Download className="h-4 w-4 shrink-0" stroke="currentColor" />
          </RuntimeLink>
        </div>
      </div>
    );
  };

  if (!centerLastRow) {
    return (
      <section className="w-full">
        {/* Cards stretch to fill each grid cell — `justify-items-start` was
            here previously and collapsed every card to its content width,
            leaving big empty gaps between columns. Default `stretch` lets
            the cards take the full column width and read as a solid grid. */}
        <div className={`grid w-full grid-flow-dense gap-5 md:gap-6 ${gridCols}`}>
          {items.map((item, i) => renderCard(item, i))}
        </div>
      </section>
    );
  }

  const fullCount = Math.floor(items.length / desktop) * desktop;
  const fullRows = items.slice(0, fullCount);
  const lastRow = items.slice(fullCount);

  return (
    <section>
      {fullRows.length > 0 && (
        <div className={`grid gap-4 ${gridCols}`}>
          {fullRows.map((item, i) => renderCard(item, i))}
        </div>
      )}
      {lastRow.length > 0 && (
        <div
          className="company-report-grid-partial-row mt-4 grid gap-4"
          style={{
            gridTemplateColumns: `repeat(${lastRow.length}, 1fr)`,
            maxWidth: `${(lastRow.length / desktop) * 100}%`,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {lastRow.map((item, i) => renderCard(item, fullCount + i))}
        </div>
      )}
    </section>
  );
}
